import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { TimeZone } from '../../time/timezone.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { GenericOrder } from './orders/generic-order.js';

export class OfflineGenericOrderCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '99548b12290d4253aada7cba5b96c211';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly order: GenericOrder,
  ) {
    super();
  }

  get $typeId(): string {
    return OfflineGenericOrderCreatedEvent.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    order: GenericOrder,
  }): OfflineGenericOrderCreatedEvent {
    return new OfflineGenericOrderCreatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.order
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineGenericOrderCreatedEvent {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getStringOrNull(map, 'creatorId'),
      deviceId: MapUtils.getStringOrNull(map, 'deviceId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      source: toEnumFromString(MapUtils.getString(map, 'source'), SyncEventSource),
      order: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'order')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'order': context.toData(this.order),
    };
  }

  clone(): this {
    return OfflineGenericOrderCreatedEvent.create({
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      source: this.source,
      order: cloneData(this.order),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
           other.creatorId === this.creatorId &&
           other.deviceId === this.deviceId &&
           TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
           other.source === this.source &&
           isSameData(other.order, this.order);
  }

  static verifyType(model: DataModel): boolean {
    return model instanceof OfflineGenericOrderCreatedEvent;
  }

  static assertType(model: DataModel): model is OfflineGenericOrderCreatedEvent {
    return model instanceof OfflineGenericOrderCreatedEvent;
  }
}

export class OfflineGenericOrderCreatedEventBuilder extends DataModelBuilder<OfflineGenericOrderCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OfflineGenericOrderCreatedEvent {
    return OfflineGenericOrderCreatedEvent.fromMap(context, map);
  }
}
